import { ArrowDownMini, ArrowUpMini, ChevronRight, Trash } from "@medusajs/icons"
import { Button, Drawer, IconButton, Input, Prompt, Text, Textarea, toast } from "@medusajs/ui"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { Link, useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Field } from "@/components/form"
import { RichTextEditor } from "@/components/rich-text"
import { DataTable, EmptyState, ErrorState, PageHeader, SaveBar, SearchInput, Section, usePage } from "@/components/ui"
import { slugify } from "@/lib/catalog"
import { formatDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"

type Page = { id: string; kind: "page" | "policy"; title: string; handle: string; body_html: string; seo_title: string | null; seo_description: string | null; published: boolean; published_at: string | null; updated_at: string }
type MenuItem = { id: string; title: string; url: string; children: MenuItem[] }
type Menu = { id: string; title: string; handle: string; items: MenuItem[]; updated_at: string }

const VisiblePill = ({ on }: { on: boolean }) => (
  <span className="tf-pill" style={on ? { background: "var(--k-ok-bg)", color: "var(--k-ok-fg)" } : { background: "var(--k-fill-strong)", color: "var(--k-text-muted)" }}>{on ? "Visible" : "Hidden"}</span>
)

// ── Pages ──────────────────────────────────────────────────────────────────

export function PagesPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const navigate = useNavigate()
  const can = useCan()
  const { data, isLoading, error } = useQuery({
    queryKey: ["content", "pages", q, pageIndex],
    placeholderData: keepPreviousData,
    queryFn: () => sdk.client.fetch<{ pages: Page[]; count: number }>("/admin/terify/content/pages", { query: { kind: "page", q: q || undefined, limit: 50, offset: pageIndex * 50 } }),
  })
  return (
    <>
      <PageHeader title="Pages" actions={can("content:write") && <Button size="small" asChild><Link to="/content/pages/new">Add page</Link></Button>} />
      <Section>
        <div className="px-4 py-3"><SearchInput value={q} onChange={setQ} placeholder="Search pages" /></div>
        {error ? (
          <ErrorState error={error} />
        ) : (
          <DataTable
            rowKey={(p) => p.id}
            rows={data?.pages}
            count={data?.count ?? 0}
            pageSize={50}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            onRowClick={(p) => navigate(`/content/pages/${p.id}`)}
            empty={<EmptyState title="Add pages to your online store" body="Write clear page titles and descriptions to improve your search engine optimization (SEO) and help customers find your website." action={can("content:write") && <Button size="small" asChild><Link to="/content/pages/new">Add page</Link></Button>} />}
            columns={[
              { key: "title", header: "Title", cell: (p) => <div className="flex flex-col"><span className="text-[13px] font-medium">{p.title}</span><span className="text-[12px] text-ui-fg-subtle">{(p.body_html || "").replace(/<[^>]+>/g, " ").trim().slice(0, 80)}</span></div> },
              { key: "vis", header: "Visibility", cell: (p) => <VisiblePill on={p.published} /> },
              { key: "updated", header: "Updated", cell: (p) => formatDate(p.updated_at, false) },
            ]}
          />
        )}
      </Section>
    </>
  )
}

/** Settings → Policies → a policy: its own URL, so the back arrow and the Policies menu item work. */
export function PolicyEditorPage() {
  const { id } = useParams()
  const { data, error, isLoading } = useQuery({ queryKey: ["content", "page", id], enabled: !!id, queryFn: () => sdk.client.fetch<{ page: Page }>(`/admin/terify/content/pages/${id}`) })
  if (error) return <ErrorState error={error} />
  if (isLoading || !data) return null
  return <PageEditor key={id} page={data.page} back="/settings/policies" />
}

export function PageEditorPage() {
  const { id: routeId } = useParams()
  const id = !routeId || routeId === "new" ? null : routeId
  const { data, error, isLoading } = useQuery({ queryKey: ["content", "page", id], enabled: !!id, queryFn: () => sdk.client.fetch<{ page: Page }>(`/admin/terify/content/pages/${id}`) })
  if (error) return <ErrorState error={error} />
  if (id && (isLoading || !data)) return null
  return <PageEditor key={id ?? "new"} page={data?.page ?? null} back="/content/pages" />
}

export function PageEditor({ page, back }: { page: Page | null; back: string }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const canWrite = useCan()("content:write")
  const base = useMemo(() => ({ title: page?.title ?? "", handle: page?.handle ?? "", body_html: page?.body_html ?? "", seo_title: page?.seo_title ?? "", seo_description: page?.seo_description ?? "", published: page?.published ?? true }), [page])
  const [d, setD] = useState(base)
  const [handleTouched, setHandleTouched] = useState(!!page)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const dirty = JSON.stringify(d) !== JSON.stringify(base)
  useEffect(() => setD(base), [base])
  const isPolicy = page?.kind === "policy"
  const save = useMutation({
    mutationFn: () => {
      const body = { kind: page?.kind ?? "page", title: d.title.trim(), handle: d.handle.trim() || slugify(d.title), body_html: d.body_html, seo_title: d.seo_title.trim() || null, seo_description: d.seo_description.trim() || null, published: d.published }
      return page ? sdk.client.fetch<{ page: Page }>(`/admin/terify/content/pages/${page.id}`, { method: "POST", body }) : sdk.client.fetch<{ page: Page }>("/admin/terify/content/pages", { method: "POST", body })
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["content"] })
      toast.success(page ? "Page saved" : "Page created")
      if (!page) navigate(`/content/pages/${r.page.id}`, { replace: true })
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const del = useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/content/pages/${page!.id}`, { method: "DELETE" }),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ["content"] }), toast.success("Page deleted"), setD(base), navigate(back, { replace: true })),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && !save.isPending && currentLocation.pathname !== nextLocation.pathname)
  const storeUrl = location.hostname === "localhost" ? "http://localhost:8000" : `https://${location.hostname.replace(/^(keel|terify)\./, "")}`

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!d.title.trim()) return toast.error("Title can't be blank.")
        save.mutate()
      }}
      className="pb-24"
    >
      <PageHeader
        back={back}
        title={page?.title || "Add page"}
        actions={
          page && (
            <>
              {page.published && <Button type="button" size="small" variant="secondary" asChild><a href={`${storeUrl}/pages/${page.handle}`} target="_blank" rel="noreferrer">View page</a></Button>}
              {canWrite && !isPolicy && <Button type="button" size="small" variant="secondary" onClick={() => setConfirmDelete(true)}>Delete</Button>}
            </>
          )
        }
      />
      <fieldset disabled={!canWrite} className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          <Section>
            <div className="flex flex-col gap-y-4 px-4 py-3">
              <Field label="Title">
                <Input value={d.title} maxLength={200} placeholder="e.g. Contact us, Sizing chart, FAQs" onChange={(e) => { const title = e.target.value; setD((x) => ({ ...x, title, handle: handleTouched || isPolicy ? x.handle : slugify(title) })) }} />
              </Field>
              <div className="flex flex-col gap-y-1.5">
                <Text size="small" weight="plus">Content</Text>
                <RichTextEditor label="Content" value={d.body_html} onChange={(body_html) => setD((x) => ({ ...x, body_html }))} minHeight={260} />
              </div>
            </div>
          </Section>
          <Section title="Search engine listing">
            <div className="flex flex-col gap-y-4 px-4 py-3">
              <div className="rounded-lg border bg-ui-bg-subtle p-3">
                <Text size="small" className="text-ui-fg-interactive">{d.seo_title || d.title || "Page title"}</Text>
                <Text size="xsmall" className="text-ui-fg-muted">{storeUrl}/pages/{d.handle || slugify(d.title) || "handle"}</Text>
                <Text size="xsmall" className="mt-1 text-ui-fg-subtle">{d.seo_description || d.body_html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160) || "Add a description to see how this page might appear in search results."}</Text>
              </div>
              <Field label="Page title" hint={`${d.seo_title.length} of 70 characters used`}><Input value={d.seo_title} maxLength={120} onChange={(e) => setD({ ...d, seo_title: e.target.value })} /></Field>
              <Field label="Meta description" hint={`${d.seo_description.length} of 160 characters used`}><Textarea rows={3} value={d.seo_description} maxLength={320} onChange={(e) => setD({ ...d, seo_description: e.target.value })} /></Field>
              <Field label="URL handle" hint={isPolicy ? "Policy addresses are fixed." : undefined}>
                <Input value={d.handle} disabled={isPolicy} onChange={(e) => { setHandleTouched(true); setD({ ...d, handle: slugify(e.target.value) || e.target.value.toLowerCase() }) }} />
              </Field>
            </div>
          </Section>
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <Section title="Visibility">
            <div className="flex flex-col gap-y-2 px-4 py-3">
              {[true, false].map((v) => (
                <label key={String(v)} className="flex items-center gap-x-2">
                  <input type="radio" name="visibility" checked={d.published === v} onChange={() => setD({ ...d, published: v })} className="accent-[color:var(--k-solid)]" />
                  <Text size="small">{v ? "Visible" : "Hidden"}</Text>
                </label>
              ))}
              {page?.published_at && d.published && <Text size="xsmall" className="text-ui-fg-muted">Visible since {formatDate(page.published_at)}</Text>}
            </div>
          </Section>
        </div>
      </fieldset>
      {canWrite && <SaveBar visible={dirty || !page} message={page ? "Unsaved changes" : "Unsaved page"} saving={save.isPending} onDiscard={() => (page ? setD(base) : navigate(back))} />}
      <Prompt open={blocker.state === "blocked"}>
        <Prompt.Content>
          <Prompt.Header><Prompt.Title>Leave page with unsaved changes?</Prompt.Title><Prompt.Description>Leaving this page will discard all unsaved changes.</Prompt.Description></Prompt.Header>
          <Prompt.Footer><Prompt.Cancel onClick={() => blocker.reset?.()}>Stay</Prompt.Cancel><Prompt.Action onClick={() => blocker.proceed?.()}>Leave page</Prompt.Action></Prompt.Footer>
        </Prompt.Content>
      </Prompt>
      <Prompt open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Prompt.Content>
          <Prompt.Header><Prompt.Title>Delete {page?.title}?</Prompt.Title><Prompt.Description>Links to this page will stop working. You can add a URL redirect to send visitors elsewhere.</Prompt.Description></Prompt.Header>
          <Prompt.Footer><Prompt.Cancel>Cancel</Prompt.Cancel><Prompt.Action onClick={() => del.mutate()}>Delete page</Prompt.Action></Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </form>
  )
}

// ── Settings → Policies ────────────────────────────────────────────────────

export function PoliciesSettingsPage() {
  const { data, error } = useQuery({ queryKey: ["content", "policies"], queryFn: () => sdk.client.fetch<{ pages: Page[] }>("/admin/terify/content/pages", { query: { kind: "policy", limit: 20 } }) })
  const navigate = useNavigate()
  if (error) return <ErrorState error={error} />
  if (!data) return null
  return (
    <>
      <h1 className="k-h1 mb-1">Policies</h1>
      <Text size="small" className="mb-6 text-ui-fg-subtle">Policies are linked in the footer of your store and at checkout.</Text>
      <Section>
        <ul className="divide-y">
          {[...data.pages].sort((a, b) => a.title.localeCompare(b.title)).map((p) => (
            <li key={p.id}>
              <button onClick={() => navigate(`/settings/policies/${p.id}`)} className="flex w-full items-center justify-between gap-x-3 px-4 py-3 text-left hover:bg-ui-bg-base-hover">
                <div>
                  <Text size="small" weight="plus">{p.title}</Text>
                  <Text size="xsmall" className="text-ui-fg-subtle">{p.body_html ? `${p.body_html.replace(/<[^>]+>/g, " ").trim().slice(0, 90)}…` : "No policy set"}</Text>
                </div>
                <div className="flex items-center gap-x-2"><VisiblePill on={p.published} /><ChevronRight className="text-ui-fg-muted" /></div>
              </button>
            </li>
          ))}
        </ul>
      </Section>
    </>
  )
}

// ── Navigation (menus) + URL redirects ─────────────────────────────────────

const countItems = (items: MenuItem[]): number => items.reduce((s, i) => s + 1 + countItems(i.children), 0)

export function MenusPage() {
  const { data, error } = useQuery({ queryKey: ["content", "menus"], queryFn: () => sdk.client.fetch<{ menus: Menu[] }>("/admin/terify/content/menus") })
  const can = useCan()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  if (error) return <ErrorState error={error} />
  return (
    <>
      <PageHeader
        title="Menus"
        actions={
          <>
            <Button size="small" variant="secondary" asChild><Link to="/content/redirects">URL redirects</Link></Button>
            {can("content:write") && <Button size="small" onClick={() => setCreating(true)}>Create menu</Button>}
          </>
        }
      />
      <Section>
        <ul className="divide-y">
          {data?.menus.map((m) => (
            <li key={m.id}>
              <button onClick={() => navigate(`/content/menus/${m.id}`)} className="flex w-full items-center justify-between gap-x-3 px-4 py-3 text-left hover:bg-ui-bg-base-hover">
                <div className="min-w-0">
                  <Text size="small" weight="plus">{m.title}</Text>
                  <Text size="xsmall" className="truncate text-ui-fg-subtle">{m.items.map((i) => i.title).join(", ") || "No menu items"}</Text>
                </div>
                <Text size="xsmall" className="shrink-0 text-ui-fg-muted">{countItems(m.items)} items</Text>
              </button>
            </li>
          ))}
        </ul>
      </Section>
      {creating && <NewMenuDrawer onClose={() => setCreating(false)} />}
    </>
  )
}

function NewMenuDrawer({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [title, setTitle] = useState("")
  const mut = useMutation({
    mutationFn: () => sdk.client.fetch<{ menu: Menu }>("/admin/terify/content/menus", { method: "POST", body: { title: title.trim(), handle: slugify(title), items: [] } }),
    onSuccess: (r) => (qc.invalidateQueries({ queryKey: ["content", "menus"] }), navigate(`/content/menus/${r.menu.id}`)),
    onError: (e) => toast.error(errorMessage(e)),
  })
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>Create menu</Drawer.Title></Drawer.Header>
        <Drawer.Body><Field label="Title" hint="A descriptive name, e.g. Sidebar menu"><Input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} /></Field></Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!title.trim()} isLoading={mut.isPending} onClick={() => mut.mutate()}>Create</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

const newId = () => Math.random().toString(36).slice(2, 10)

export function MenuEditorPage() {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const canWrite = useCan()("content:write")
  const { data, error } = useQuery({ queryKey: ["content", "menu", id], queryFn: () => sdk.client.fetch<{ menu: Menu }>(`/admin/terify/content/menus/${id}`) })
  const links = useLinkSuggestions()
  const [m, setM] = useState<{ title: string; items: MenuItem[] } | null>(null)
  const [editing, setEditing] = useState<{ path: number[]; item: MenuItem } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  useEffect(() => {
    if (data) setM({ title: data.menu.title, items: data.menu.items })
  }, [data])
  const save = useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/content/menus/${id}`, { method: "POST", body: { title: m!.title.trim(), handle: data!.menu.handle, items: m!.items } }),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ["content"] }), toast.success("Menu saved")),
    onError: (e) => toast.error(errorMessage(e)),
  })
  const del = useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/content/menus/${id}`, { method: "DELETE" }),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ["content"] }), toast.success("Menu deleted"), navigate("/content/menus", { replace: true })),
    onError: (e) => toast.error(errorMessage(e)),
  })
  if (error) return <ErrorState error={error} />
  if (!data || !m) return null
  const core = ["main-menu", "footer"].includes(data.menu.handle)
  const dirty = JSON.stringify(m) !== JSON.stringify({ title: data.menu.title, items: data.menu.items })

  // tree helpers (two levels, like most Shopify themes)
  const update = (fn: (items: MenuItem[]) => MenuItem[]) => setM((x) => ({ ...x!, items: fn(structuredClone(x!.items)) }))
  const move = (path: number[], dir: -1 | 1) =>
    update((items) => {
      const list = path.length === 1 ? items : items[path[0]].children
      const i = path[path.length - 1]
      const j = i + dir
      if (j < 0 || j >= list.length) return items
      ;[list[i], list[j]] = [list[j], list[i]]
      return items
    })
  const remove = (path: number[]) => update((items) => (path.length === 1 ? items.filter((_, i) => i !== path[0]) : (items[path[0]].children.splice(path[1], 1), items)))
  const upsert = (path: number[] | null, item: MenuItem, parent?: number) =>
    update((items) => {
      if (path) {
        if (path.length === 1) items[path[0]] = { ...item, children: items[path[0]].children }
        else items[path[0]].children[path[1]] = { ...item, children: [] }
      } else if (parent != null) items[parent].children.push({ ...item, children: [] })
      else items.push({ ...item, children: [] })
      return items
    })

  const Row = ({ item, path }: { item: MenuItem; path: number[] }) => (
    <li className={"flex items-center gap-x-2 px-4 py-2.5 " + (path.length > 1 ? "pl-10" : "")}>
      <div className="min-w-0 flex-1">
        <Text size="small" weight="plus">{item.title}</Text>
        <Text size="xsmall" className="truncate text-ui-fg-subtle">{item.url}</Text>
      </div>
      {canWrite && (
        <div className="flex items-center gap-x-0.5">
          <IconButton type="button" size="small" variant="transparent" aria-label="Move up" onClick={() => move(path, -1)}><ArrowUpMini /></IconButton>
          <IconButton type="button" size="small" variant="transparent" aria-label="Move down" onClick={() => move(path, 1)}><ArrowDownMini /></IconButton>
          <Button type="button" size="small" variant="transparent" onClick={() => setEditing({ path, item })}>Edit</Button>
          <IconButton type="button" size="small" variant="transparent" aria-label={`Delete ${item.title}`} onClick={() => remove(path)}><Trash /></IconButton>
        </div>
      )}
    </li>
  )

  return (
    <div className="pb-24">
      <PageHeader back="/content/menus" title={data.menu.title} actions={canWrite && !core && <Button size="small" variant="secondary" onClick={() => setConfirmDelete(true)}>Delete menu</Button>} />
      <div className="flex flex-col gap-4">
        <Section>
          <div className="px-4 py-3"><Field label="Title"><Input value={m.title} disabled={!canWrite} maxLength={100} onChange={(e) => setM({ ...m, title: e.target.value })} /></Field></div>
          {core && <Text size="xsmall" className="px-4 py-2 text-ui-fg-subtle">Your storefront shows this menu in the {data.menu.handle === "main-menu" ? "header" : "footer"}.</Text>}
        </Section>
        <Section title="Menu items">
          {m.items.length ? (
            <ul className="divide-y">
              {m.items.map((it, i) => (
                <li key={it.id}>
                  <ul className="divide-y">
                    <Row item={it} path={[i]} />
                    {it.children.map((c, j) => <Row key={c.id} item={c} path={[i, j]} />)}
                    {canWrite && (
                      <li className="py-1 pl-10">
                        <Button type="button" size="small" variant="transparent" onClick={() => setEditing({ path: [-1, i], item: { id: newId(), title: "", url: "", children: [] } })}>+ Add menu item to {it.title}</Button>
                      </li>
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="This menu doesn't have any items" />
          )}
          {canWrite && (
            <div className="px-4 py-3">
              <Button type="button" size="small" variant="secondary" onClick={() => setEditing({ path: [], item: { id: newId(), title: "", url: "", children: [] } })}>+ Add menu item</Button>
            </div>
          )}
        </Section>
      </div>
      {canWrite && <SaveBar visible={dirty} message="Unsaved changes" saving={save.isPending} onDiscard={() => setM({ title: data.menu.title, items: data.menu.items })} onSave={() => save.mutate()} />}
      {editing && (
        <MenuItemDrawer
          item={editing.item}
          links={links}
          onClose={() => setEditing(null)}
          onSave={(item) => {
            if (editing.path.length === 0) upsert(null, item)
            else if (editing.path[0] === -1) upsert(null, item, editing.path[1])
            else upsert(editing.path, item)
            setEditing(null)
          }}
        />
      )}
      <Prompt open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Prompt.Content>
          <Prompt.Header><Prompt.Title>Delete {data.menu.title}?</Prompt.Title><Prompt.Description>This can't be undone.</Prompt.Description></Prompt.Header>
          <Prompt.Footer><Prompt.Cancel>Cancel</Prompt.Cancel><Prompt.Action onClick={() => del.mutate()}>Delete menu</Prompt.Action></Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </div>
  )
}

/** Quick links like Shopify's link picker: home, catalogue, collections, pages, policies. */
function useLinkSuggestions() {
  return useQuery({
    queryKey: ["content", "link-suggestions"],
    staleTime: 60_000,
    queryFn: async () => {
      const [cols, pages] = await Promise.all([
        sdk.admin.productCollection.list({ limit: 100, fields: "title,handle" } as any).catch(() => ({ collections: [] as any[] })),
        sdk.client.fetch<{ pages: Page[] }>("/admin/terify/content/pages", { query: { limit: 200 } }).catch(() => ({ pages: [] as Page[] })),
      ])
      return [
        { label: "Home", url: "/" },
        { label: "All products", url: "/collections/all" },
        { label: "Search", url: "/search" },
        ...(cols as any).collections.map((c: any) => ({ label: `Collection: ${c.title}`, url: `/collections/${c.handle}` })),
        ...pages.pages.map((p) => ({ label: `${p.kind === "policy" ? "Policy" : "Page"}: ${p.title}`, url: `/pages/${p.handle}` })),
      ]
    },
  }).data ?? []
}

const validUrl = (u: string) => /^\/(?!\/)[^\s\\]*$/.test(u) || /^https:\/\/[^\s]+$/i.test(u) || /^mailto:[^\s]+$/i.test(u) || /^tel:\+?[0-9 ()-]+$/i.test(u)

function MenuItemDrawer({ item, links, onClose, onSave }: { item: MenuItem; links: { label: string; url: string }[]; onClose: () => void; onSave: (i: MenuItem) => void }) {
  const [title, setTitle] = useState(item.title)
  const [url, setUrl] = useState(item.url)
  const ok = title.trim() && validUrl(url.trim())
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>{item.title ? "Edit menu item" : "Add menu item"}</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4">
          <Field label="Label"><Input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="Link" hint="Pick a page below, or enter /a-path or an https:// link">
            <Input value={url} maxLength={500} placeholder="Search or paste a link" onChange={(e) => setUrl(e.target.value)} />
          </Field>
          {url.trim() && !validUrl(url.trim()) && <Text size="xsmall" className="text-ui-fg-error">Use a path like /pages/about or a full https:// link.</Text>}
          <div>
            <Text size="xsmall" weight="plus" className="mb-1 text-ui-fg-subtle">Quick links</Text>
            <div className="max-h-64 overflow-y-auto rounded-lg border">
              {links
                .filter((l) => !url || l.label.toLowerCase().includes(url.toLowerCase()) || l.url.includes(url))
                .slice(0, 40)
                .map((l) => (
                  <button
                    key={l.url + l.label}
                    type="button"
                    onClick={() => {
                      setUrl(l.url)
                      if (!title.trim()) setTitle(l.label.replace(/^(Collection|Page|Policy): /, ""))
                    }}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-[13px] hover:bg-ui-bg-base-hover"
                  >
                    <span>{l.label}</span>
                    <span className="text-[12px] text-ui-fg-muted">{l.url}</span>
                  </button>
                ))}
            </div>
          </div>
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!ok} onClick={() => onSave({ ...item, title: title.trim(), url: url.trim() })}>{item.title ? "Done" : "Add"}</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

export function RedirectsPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const qc = useQueryClient()
  const can = useCan()
  const [adding, setAdding] = useState(false)
  const { data, isLoading, error } = useQuery({
    queryKey: ["content", "redirects", q, pageIndex],
    placeholderData: keepPreviousData,
    queryFn: () => sdk.client.fetch<{ redirects: { id: string; from_path: string; to_path: string }[]; count: number }>("/admin/terify/content/redirects", { query: { q: q || undefined, limit: 50, offset: pageIndex * 50 } }),
  })
  const del = useMutation({
    mutationFn: (id: string) => sdk.client.fetch(`/admin/terify/content/redirects/${id}`, { method: "DELETE" }),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ["content", "redirects"] }), toast.success("Redirect deleted")),
    onError: (e) => toast.error(errorMessage(e)),
  })
  return (
    <>
      <PageHeader back="/content/menus" title="URL redirects" actions={can("content:write") && <Button size="small" onClick={() => setAdding(true)}>Create URL redirect</Button>} />
      <Section>
        <div className="px-4 py-3"><SearchInput value={q} onChange={setQ} placeholder="Search redirects" /></div>
        {error ? (
          <ErrorState error={error} />
        ) : (
          <DataTable
            rowKey={(r) => r.id}
            rows={data?.redirects}
            count={data?.count ?? 0}
            pageSize={50}
            pageIndex={pageIndex}
            onPageChange={setPage}
            loading={isLoading}
            empty={<EmptyState title="No URL redirects" body="When you change a page or product URL, add a redirect so old links keep working." />}
            columns={[
              { key: "from", header: "Redirect from", cell: (r) => <span className="text-[13px]">{r.from_path}</span> },
              { key: "to", header: "Redirect to", cell: (r) => <span className="text-[13px] text-ui-fg-subtle">{r.to_path}</span> },
              {
                key: "del",
                header: "",
                className: "w-10",
                cell: (r) => can("content:write") && <IconButton size="small" variant="transparent" aria-label={`Delete redirect ${r.from_path}`} onClick={() => del.mutate(r.id)}><Trash /></IconButton>,
              },
            ]}
          />
        )}
      </Section>
      {adding && <RedirectDrawer onClose={() => setAdding(false)} />}
    </>
  )
}

function RedirectDrawer({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const mut = useMutation({
    mutationFn: () => sdk.client.fetch("/admin/terify/content/redirects", { method: "POST", body: { from_path: from.trim(), to_path: to.trim() } }),
    onSuccess: () => (qc.invalidateQueries({ queryKey: ["content", "redirects"] }), toast.success("URL redirect created"), onClose()),
    onError: (e) => toast.error(errorMessage(e)),
  })
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header><Drawer.Title>Create URL redirect</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4">
          <Field label="Redirect from" hint="An old path on your store, e.g. /pages/old-about"><Input value={from} placeholder="/" onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="Redirect to" hint="A path on your store or an https:// link"><Input value={to} placeholder="/" onChange={(e) => setTo(e.target.value)} /></Field>
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" disabled={!/^\/(?!\/)/.test(from.trim()) || !(/^\/(?!\/)/.test(to.trim()) || /^https:\/\//i.test(to.trim()))} isLoading={mut.isPending} onClick={() => mut.mutate()}>Save redirect</Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}
